import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

const openDialogs = new Set();
let bodyState;
function lock(component) {
  if (!openDialogs.size) {
    bodyState = { scroll: window.scrollY, position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${bodyState.scroll}px`;
    document.body.style.width = '100%';
  }
  openDialogs.add(component);
}
function unlock(component) {
  openDialogs.delete(component);
  if (!openDialogs.size && bodyState) {
    Object.assign(document.body.style, { position: bodyState.position, top: bodyState.top, width: bodyState.width });
    window.scrollTo({ top: bodyState.scroll, behavior: 'instant' });
    bodyState = undefined;
  }
}
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #events;
  #dragEvents;
  #drag;
  #closing;
  #previousFocus;
  #suppressClick = false;
  connectedCallback() {
    super.connectedCallback();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.addEventListener('click', this.#click, options);
    this.refs.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeDialog(); }, options);
    this.refs.dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const items = [...this.refs.dialog.querySelectorAll('a[href], button:enabled, input:enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex="-1"])')]
        .filter(element => element.tabIndex >= 0 && element.getClientRects().length && !element.closest('[hidden], [inert]') && getComputedStyle(element).visibility !== 'hidden');
      const first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.refs.dialog)) {
        event.preventDefault(); last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus({ preventScroll: true });
      }
    }, options);
    this.refs.dialog.addEventListener('close', this.#closed, options);
    window.addEventListener('resize', this.#resize, options);
    window.addEventListener('pagehide', () => { this.#teardownDrag(); this.refs.dialog.close(); this.#closed(); }, options);
    const settle = () => {
      if (!prefersReducedMotion()) return;
      this.#teardownDrag();
      this.refs.dialog.getAnimations().forEach(animation => animation.finish());
    };
    document.addEventListener('eido:a11y-change', settle, options);
    matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', settle, options);
  }
  disconnectedCallback() {
    this.#events?.abort();
    this.#resize.cancel();
    this.#teardownDrag();
    unlock(this);
    super.disconnectedCallback();
  }
  #resize = debounce(() => {
    if ((this.minWidth && innerWidth < this.minWidth) || (this.maxWidth && innerWidth > this.maxWidth)) this.closeDialog();
  }, 50);
  showDialog(trigger) {
    const { dialog } = this.refs;
    if (dialog.open) return;
    const opener = trigger instanceof HTMLElement ? trigger : trigger?.target?.closest('button, a[href], [tabindex]');
    this.#previousFocus = opener || document.activeElement;
    lock(this);
    try { dialog.showModal(); }
    catch (error) { unlock(this); throw error; }
    this.dispatchEvent(new DialogOpenEvent());
    this.#setupDrag();
  }
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open || this.#closing) return this.#closing;
    this.#teardownDrag();
    dialog.classList.add('dialog-closing');
    this.#closing = (async () => {
      try { if (!prefersReducedMotion()) await onAnimationEnd(dialog, undefined, { subtree: false }); }
      finally {
        dialog.close();
        this.#closed();
      }
    })();
    try { return await this.#closing; }
    finally { this.#closing = undefined; }
  };
  #closed = () => {
    const wasOpen = openDialogs.has(this);
    this.#teardownDrag();
    this.refs.dialog.classList.remove('dialog-closing');
    unlock(this);
    if (wasOpen) {
      if (this.#previousFocus?.isConnected && !this.#previousFocus.closest('[inert]')) this.#previousFocus.focus({ preventScroll: true });
      this.#previousFocus = undefined;
      this.dispatchEvent(new DialogCloseEvent());
    }
  };
  toggleDialog = event => this.refs.dialog.open ? this.closeDialog() : this.showDialog(event);
  #click = event => {
    if (this.#suppressClick) { this.#suppressClick = false; event.preventDefault(); return; }
    if (this.refs.dialog.open && !this.#drag && isClickedOutside(event, this.refs.dialog)) this.closeDialog();
  };
  #setupDrag() {
    const { dialog } = this.refs;
    if (!matchMedia('(max-width: 1023.98px)').matches || !dialog.matches('.dialog-drawer, .popup-link__content--drawer')) return;
    this.#dragEvents = new AbortController();
    const signal = this.#dragEvents.signal;
    dialog.addEventListener('pointerdown', event => {
      if (prefersReducedMotion() || event.pointerType === 'mouse' || event.target.closest('button,a,input,select,textarea')) return;
      const scroller = dialog.querySelector('.popup-link__inner') || dialog;
      if (scroller.scrollTop || event.clientY - dialog.getBoundingClientRect().top > 44) return;
      this.#drag = { id: event.pointerId, y: event.clientY, distance: 0 };
      dialog.style.transition = 'none';
      dialog.style.willChange = 'transform';
      dialog.setPointerCapture(event.pointerId);
    }, { signal, passive: true });
    dialog.addEventListener('pointermove', event => {
      if (!this.#drag || event.pointerId !== this.#drag.id) return;
      this.#drag.distance = Math.max(0, event.clientY - this.#drag.y);
      dialog.style.transform = `translateY(${this.#drag.distance}px)`;
      if (this.#drag.distance > 6) { this.#suppressClick = true; event.preventDefault(); }
    }, { signal, passive: false });
    const end = event => {
      if (!this.#drag || event.pointerId !== this.#drag.id) return;
      const dismiss = event.type !== 'pointercancel' && this.#drag.distance > 120;
      const distance = this.#drag.distance;
      this.#drag = undefined;
      dialog.style.transition = '';
      dialog.style.transform = '';
      dialog.style.willChange = '';
      if (dismiss) this.closeDialog();
      else if (!prefersReducedMotion()) dialog.animate([{ transform: `translateY(${distance}px)` }, { transform: 'none' }], { duration: 160, easing: 'ease-out' });
    };
    dialog.addEventListener('pointerup', end, { signal });
    dialog.addEventListener('pointercancel', end, { signal });
  }
  #teardownDrag() {
    this.#dragEvents?.abort();
    this.#drag = undefined;
    const dialog = this.refs.dialog;
    if (dialog) { dialog.style.transition = ''; dialog.style.transform = ''; dialog.style.willChange = ''; }
  }
  get minWidth() { return Number(this.getAttribute('dialog-active-min-width')); }
  get maxWidth() { return Number(this.getAttribute('dialog-active-max-width')); }
}
if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);
export class DialogOpenEvent extends CustomEvent {
  constructor() { super(DialogOpenEvent.eventName); }
  static eventName = 'dialog:open';
}
export class DialogCloseEvent extends CustomEvent {
  constructor() { super(DialogCloseEvent.eventName); }
  static eventName = 'dialog:close';
}
document.addEventListener('toggle', event => {
  if ((event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) && event.target.hasAttribute('scroll-lock')) {
    document.documentElement.toggleAttribute('scroll-lock', Boolean(document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]')));
  }
}, { capture: true });
